import { type KeyboardEvent, useCallback, useRef } from 'react';

import {
  isClosedSelectTarget,
  isTypeAheadKey,
  nextTypeAhead,
  type TypeAheadBuffer,
  type TypeAheadKeys,
} from '@/utils/typeAhead';

/**
 * Lets a closed MUI select answer to typing, like a native one: "P" then "E" picks PE. Returns a
 * handler factory for the field's root, so it also works inside a `Controller` render:
 * `onKeyDown={typeAhead(options, field.value, field.onChange)}`; `getKeys` searches an option by
 * more than its text (a state's name and its UF). Once open, the menu list searches by itself
 * and its keys are left alone.
 */
export function useSelectTypeAhead() {
  const typed = useRef<TypeAheadBuffer | null>(null);

  return useCallback(
    <Option extends string>(
      options: readonly Option[],
      value: string | null | undefined,
      onPick: (option: Option) => void,
      getKeys?: TypeAheadKeys<Option>,
    ) =>
      (event: KeyboardEvent<HTMLElement>) => {
        if (event.defaultPrevented || !isClosedSelectTarget(event.target) || !isTypeAheadKey(event))
          return;
        const next = nextTypeAhead(options, typed.current, event.key, Date.now(), getKeys);
        typed.current = next.buffer;
        if (!next.option) return;
        event.preventDefault();
        if (next.option !== value) onPick(next.option);
      },
    [],
  );
}
